import useTypedSelector from 'interfaces/useTypedSelector'
import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import StorefrontProduct from '@local-types/products/storefrontProduct'
import { useActiveCompany } from 'hooks/useActiveCompany/useActiveCompany'
import {
	shouldPerformUnAuthenticatedProductFetch,
	shouldPerformAuthenticatedProductFetch,
} from 'helpers/productHelpers/productHelpers'
import { fetchProductsByCategoryId } from 'reducers/productReducer'

interface ProductsInfo {
	products: StorefrontProduct[]
	loading: boolean
	lazyLoading: boolean
	error: boolean
	totalRecords: number
	pageSize: number
}

const PAGE_SIZE = 10

const useProductsByCategoryId = (categoryId: string, page?: number): ProductsInfo => {
	const dispatch = useDispatch()
	const activePage = useRef(0)
	const { authState } = useTypedSelector(({ auth }) => auth)
	const { activeRelation, state: userInfoState } = useActiveCompany()
	const { byCategoryId } = useTypedSelector(({ products }) => products)
	const status = byCategoryId[categoryId]?.status
	const products = byCategoryId[categoryId]?.products
	const companyId = byCategoryId[categoryId]?.companyId
	const totalRecords = byCategoryId[categoryId]?.totalRecords || 0

	useEffect(() => {
		const currentPage = page || 1
		const dispatchUnAuthenticatedFetch = shouldPerformUnAuthenticatedProductFetch(
			authState,
			userInfoState,
			activeRelation
		)
		const dispatchAuthenticatedFetch = shouldPerformAuthenticatedProductFetch(
			userInfoState,
			companyId,
			activeRelation,
			activePage.current,
			currentPage
		)

		if (dispatchUnAuthenticatedFetch) dispatch(fetchProductsByCategoryId(categoryId, currentPage))
		else if (dispatchAuthenticatedFetch)
			dispatch(fetchProductsByCategoryId(categoryId, currentPage, activeRelation?.company.id))

		// Update the value of our active page
		activePage.current = currentPage
	}, [authState, companyId, activeRelation, userInfoState, page])

	return {
		products: products || [],
		loading: status === 'pending',
		lazyLoading: status === 'additional-page-pending',
		error: status === 'failed',
		totalRecords,
		pageSize: PAGE_SIZE,
	}
}

export default useProductsByCategoryId
